import { Box, FormControl, FormLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { get } from "lodash";
import { default as Moment } from 'moment';
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import branch from "../../services/branch";
import service from "../../services/group";
import { rupiah } from "../../utils/currency";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function BranchGroupDetail() {
  const location = useLocation();
  const path = location.pathname.split("/");
  const group_id = path.pop() || "";
  const [valueFrom, setValueFrom] = useState<Dayjs | null>(dayjs(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [valueTo, setValueTo] = useState<Dayjs | null>(dayjs(new Date()));
  const [countTotal, setCountTotal] = useState(0);
  const [countSales, setCountSales] = useState(0);

  const currentGroup = useQuery(
    ["current", group_id],
    () => service.getDetailBranchGroup(group_id),
    {
      ...queryDefault,
    }
  );

  const [countStats, setCountStats] = useState([
    { grouped_date: "", total_kertas: 0 },
  ]);
  const [salesStats, setSalesStats] = useState([
    { grouped_date: "", subtotal: 0 },
  ]);
  const chartSetting = {
    height: 300,
  };

  const [options, setOptions] = useState<any>({
    group_id: group_id,
    startDate: Moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'),
    endDate: Moment(new Date()).format('YYYY-MM-DD'),
  });

  const form = useForm({
    defaultValues: options,
  });


  const listBranchStats = useQuery(
    ["stats", options],
    ({ queryKey }) => {
      return branch.getBranchStatsRange({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  useEffect(() => {
    if (listBranchStats.data?.data?.count?.length > 0) {
      var countStats: any = [];
      var countTotalTemp = 0;
      for (const i of listBranchStats.data.data.count) {
        countStats.push({
          grouped_date: `${i.grouped_date.split('-')[2]}-${i.grouped_date.split('-')[1]}`,
          total_kertas: Number(i.total_kertas)
        });
        countTotalTemp = countTotalTemp + Number(i.total_kertas);
      }
      setCountStats(countStats);
      setCountTotal(countTotalTemp);
    } else {
      setCountStats([]);
      setCountTotal(0);
    }
    if (listBranchStats.data?.data?.sales?.length > 0) {
      var salesStats: any = [];
      var countSalesTemp = 0;
      for (const i of listBranchStats.data.data.sales) {
        salesStats.push({
          grouped_date: `${i.grouped_date.split('-')[2]}-${i.grouped_date.split('-')[1]}`,
          subtotal: Number(i.subtotal)
        });
        countSalesTemp = countSalesTemp + Number(i.subtotal);
      }
      setSalesStats(salesStats);
      setCountSales(countSalesTemp)
    } else {
      setSalesStats([]);
      setCountSales(0);
    }
  }, [listBranchStats]);

  useEffect(() => {
    form.setValue("startDate", Moment(valueFrom?.format('YYYY-MM-DD')).format('YYYY-MM-DD'));
    form.setValue("endDate", Moment(valueTo?.format('YYYY-MM-DD')).format('YYYY-MM-DD'));
    setOptions({ ...options, startDate: form.getValues().startDate, endDate: form.getValues().endDate });
  }, [valueFrom, valueTo, form]);

  return (
    <MainLayout>
      <Helmet>
        <title>Group Detail</title>
      </Helmet>

      <Breadcrumb
        breadcrumbs={[{ label: "Group", href: "/app/branch-group" }]}
        label={currentGroup.data?.data?.name}
        href={`/app/branch-group/`}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={{ lg: 4, md: 2 }}>
            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Name
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {currentGroup.data?.data?.name}
                </Typography>
              </Stack>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Percentage
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {currentGroup.data?.data?.percentage ?? 0}
                </Typography>
              </Stack>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Region
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {currentGroup.data?.data?.region}
                </Typography>
              </Stack>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Kode Cabang
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {currentGroup.data?.data?.kode_cabang}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h2">Stats</Typography>

          <Box my={2}>
            <Stack direction="row" spacing={1}>
              <FormControl margin="normal">
                <FormLabel>Start Date</FormLabel>
                <Controller
                  name="date_from"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                        value={valueFrom}
                        onChange={newValue => {
                          setValueFrom(newValue);
                        }}
                        renderInput={props => <TextField {...props} />}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>

              <FormControl margin="normal">
                <FormLabel>End Date</FormLabel>
                <Controller
                  name="date_to"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                        value={valueTo}
                        onChange={newValue => {
                          setValueTo(newValue);
                        }}
                        renderInput={props => <TextField {...props} />}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>
            </Stack>
          </Box>
          <Typography variant="h3" sx={{ mt: 2 }}>Total Kertas ({countTotal})</Typography>
          <BarChart
            sx={{ "& .MuiChartsAxis-tickLabel tspan": { fontSize: "10px" } }}
            dataset={countStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_date",
                label: "Date-Month",
              },
            ]}
            series={[{ dataKey: "total_kertas", label: "Total" }]}
            {...chartSetting}
          />
          <Typography variant="h3" sx={{ mt: 2 }}>Revenue ({rupiah(countSales)})</Typography>
          <BarChart
            sx={{ "& .MuiChartsAxis-tickLabel tspan": { fontSize: "10px" } }}
            dataset={salesStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_date",
                label: "Date-Month",
              },
            ]}
            series={[{ dataKey: "subtotal", label: "Total" }]}
            {...chartSetting}
          />
        </Box>

        <Typography sx={{
          fontWeight: 'bold',
          mt: 2
        }}>Branches</Typography>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={currentGroup.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(currentGroup, "data.branches", [])}
            rowCount={get(currentGroup, "data.branches", []).length}
            disableSelectionOnClick
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}

            columns={[
              {
                field: "name",
                headerName: "Branch Name",
                flex: 1,
                filterable: false,
                minWidth: 150,
              },
              {
                field: "is_shared_printer",
                headerName: "Shared Printer",
                flex: 1,
                filterable: false,
                minWidth: 150,
              }
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
